import { FC, useEffect, useMemo, useState } from 'react'

// Imports diretos (e não pelos barrels): o barril de componentes arrasta o app inteiro e torna este
// bloco impossível de testar isoladamente. Mesmo padrão do `EsusCorrigirLotacaoModal` (E5d).
import { ButtonType } from '../../../../../_metronic/helpers/enumsUI'
import { showAlert, showError } from '../../../../../_metronic/helpers/AlertHelper'
import { Button } from '../../../../components/Button/Button'
import { Label } from '../../../../components/Label/Label'
import { useModal } from '../../../../components/Modal/ModalContext'
import { sanitizar } from '../envios/core/_esusErro'
import { rotuloStatus } from '../envios/core/_models'
import { MSG_403_SESSAO_ANTERIOR } from './core/_esusCadastroMensagens'
import { EsusCadastroLotacao, rotuloLotacaoCadastro } from './core/_models'
import { enviarEsusCadastro, getContextoEsusCadastro } from './core/_requests'

type Props = {
  pacienteId: number
  pacienteNome?: string | null
}

/** Id do `select`, usado também pelo `htmlFor` do rótulo (AC8). */
const ID_SELETOR = 'lotacao-cadastro-esus'

/**
 * Envio manual da Ficha de Cadastro Individual do cidadão ao PEC e-SUS APS (F7b/F7c).
 *
 * A lotação é escolhida **no momento do envio** (decisão do Jáder, 2026-09-06): é ela que assina a
 * ficha e define para qual estabelecimento o cadastro vai. Não existe "última usada" aqui — o
 * contexto da F7b não traz esse campo, ao contrário do de encerramento (E5c).
 *
 * O `select` é nativo, e não o `CustomSelect` dos vizinhos, por dois motivos: ele não expõe o id do
 * input do react-select (rótulo não associa, reprovaria a a11y) e o `<option disabled>` nativo
 * resolve "lotação com integração desativada não é selecionável" sem componente novo.
 *
 * Nenhuma mensagem vinda da API é renderizada sem passar por `sanitizar()`, e nenhuma é injetada
 * como HTML: o `showError` do repositório monta HTML e injeta via `html:` do Swal, então o 422 —
 * único erro cujo texto é acionável pelo usuário — é tratado aqui como texto.
 *
 * Contraste (achado de uso real de 2026-09-07): os textos deste diálogo não usam a cor da própria
 * variante do alerta nem `text-muted`. O `.alert-{variante}` do tema pinta o texto com
 * `--bs-{variante}` sobre `--bs-{variante}-light` (`core/components/_alert.scss`), o que no tema
 * claro dá 1,43:1 no `warning` (amarelo sobre amarelo-claro) e no escuro dá 2,31:1 no `info` — e
 * `text-muted` é `gray-500` (2,45:1 sobre branco). Por isso todo texto daqui é `text-gray-800` /
 * `text-gray-700`: são tokens que **invertem** entre claro e escuro (`_variables.scss` x
 * `_variables-dark.scss`), no mesmo remédio que os campos de login já adotaram em
 * `sass/components/_variables.custom.scss`. Fundo e borda continuam os da variante, que é o que dá
 * a semântica de aviso/informação.
 */
export const EsusCadastroPacienteModal: FC<Props> = ({ pacienteId, pacienteNome }) => {
  const { closeModal } = useModal()

  const [lotacoes, setLotacoes] = useState<EsusCadastroLotacao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [escolhida, setEscolhida] = useState<number | ''>('')
  const [aviso, setAviso] = useState<{ tipo: 'info' | 'warning'; texto: string } | null>(null)

  useEffect(() => {
    let ativo = true
    getContextoEsusCadastro(pacienteId)
      .then((dados) => {
        if (!ativo) return
        const recebidas = dados?.lotacoes || []
        setLotacoes(recebidas)
        // Pré-seleção: a primeira **selecionável** da lista, que já vem em ordenação estável da API.
        const primeira = recebidas.find((lotacao) => lotacao.esusAtivo)
        setEscolhida(primeira ? primeira.id : '')
      })
      .catch((err) => {
        if (!ativo) return
        if (err?.response?.status === 403) {
          setAviso({ tipo: 'warning', texto: MSG_403_SESSAO_ANTERIOR })
        } else {
          showError(err)
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false)
      })
    return () => {
      ativo = false
    }
  }, [pacienteId])

  const selecionaveis = useMemo(() => lotacoes.filter((lotacao) => lotacao.esusAtivo), [lotacoes])

  const confirmar = async () => {
    if (!escolhida) return
    setEnviando(true)
    setAviso(null)
    try {
      const resultado = await enviarEsusCadastro(pacienteId, Number(escolhida))

      if (resultado?.desfecho === 'SEM_ALTERACAO') {
        // 2xx: nada mudou desde o último cadastro aceito. Não é falha — o diálogo continua aberto,
        // sem ícone nem estilo de erro.
        setAviso({
          tipo: 'info',
          texto:
            'Nada mudou no cadastro deste cidadão desde o último envio aceito pelo PEC, ' +
            'então nenhuma ficha nova foi gerada.',
        })
        return
      }

      closeModal()
      showAlert({
        title: 'Cadastro enviado',
        text: `A ficha de cadastro individual entrou na fila de envio ao e-SUS APS (situação: ${rotuloStatus(
          { status: resultado?.status || undefined }
        )}).`,
        icon: 'success',
      })
    } catch (err: any) {
      const status = err?.response?.status
      const corpo = err?.response?.data?.data

      if (status === 422) {
        // Texto acionável do backend ("escolha outra lotação", "a unidade está sem CNES"), sempre
        // sanitizado antes de qualquer render e nunca como HTML.
        const texto = corpo?.errors?.lotacaoId?.[0] || corpo?.message
        setAviso({
          tipo: 'warning',
          texto:
            sanitizar(texto) ||
            'A lotação escolhida não pode assinar este cadastro. Escolha outra e tente de novo.',
        })
      } else if (status === 403) {
        setAviso({ tipo: 'warning', texto: MSG_403_SESSAO_ANTERIOR })
      } else {
        showError(err)
      }
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) {
    return <div className='p-5 text-center text-gray-700'>Carregando lotações...</div>
  }

  const semLotacaoUtil = selecionaveis.length === 0

  return (
    <div>
      <p className='text-gray-700 fs-7 mb-5'>
        A ficha de cadastro individual{pacienteNome ? ` de ${pacienteNome}` : ''} é enviada ao PEC
        e-SUS APS assinada pela lotação escolhida abaixo — é ela que define o profissional, o CBO e
        o estabelecimento do cadastro.
      </p>

      {aviso && (
        <div
          className={`alert alert-${aviso.tipo} text-gray-800`}
          role={aviso.tipo === 'warning' ? 'alert' : 'status'}
          aria-live='polite'
        >
          {aviso.texto}
        </div>
      )}

      {semLotacaoUtil ? (
        <div className='alert alert-warning text-gray-800' role='status'>
          {lotacoes.length === 0
            ? 'Nenhuma lotação sua foi encontrada para enviar este cadastro. Cadastre uma lotação do profissional na unidade (Cadastro do profissional → Lotações) e volte aqui.'
            : 'Nenhuma lotação sua está com a integração e-SUS APS ativa. Ative a integração na unidade (Unidades → e-SUS APS) e volte aqui.'}
        </div>
      ) : (
        <>
          <Label className='pb-2' htmlFor={ID_SELETOR} isRequired>
            Lotação
          </Label>
          <select
            id={ID_SELETOR}
            className='form-select'
            value={escolhida}
            disabled={enviando}
            onChange={(e) => setEscolhida(e.target.value ? Number(e.target.value) : '')}
          >
            {lotacoes.map((lotacao) => (
              <option key={lotacao.id} value={lotacao.id} disabled={!lotacao.esusAtivo}>
                {rotuloLotacaoCadastro(lotacao)}
                {lotacao.esusAtivo ? '' : ' — integração e-SUS desativada nesta unidade'}
              </option>
            ))}
          </select>
        </>
      )}

      <div className='d-flex flex-center flex-row-fluid pt-10'>
        <Button type={ButtonType.Reset} className='btn-light me-3' onClick={closeModal}>
          Cancelar
        </Button>
        <Button
          type={ButtonType.Button}
          className='btn-primary'
          disabled={!escolhida || enviando || semLotacaoUtil}
          onClick={confirmar}
        >
          Enviar cadastro
        </Button>
      </div>
    </div>
  )
}
